import React, { useState } from "react";
import SubsectionAdmin from "./SubsectionAdmin";
import { Pencil, Trash2, Plus, X, Check } from "lucide-react";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const ModuleAdmin = ({ course }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  const deleteModule = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    await fetch(`${apiBaseURL}/modules/${id}`, { method: "DELETE" });
    window.location.reload(); // refresh course tree
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">Modules</h3>
        <button
          className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
          onClick={() => {
            setEditingModule(null);
            setFormOpen(true);
          }}
        >
          <Plus className="w-4 h-4" />
          Add Module
        </button>
      </div>

      {/* Module List */}
      {course.modules.map((module) => (
        <div
          key={module.id}
          className="border rounded-lg bg-white shadow-sm p-4 space-y-3"
        >
          {/* Module Header */}
          <div className="flex justify-between items-center">
            <div>
              <strong className="text-gray-900">{module.title}</strong>{" "}
              <span className="text-gray-500">({module.slug})</span>
            </div>
            <div className="flex gap-3">
              <button
                className="text-yellow-600 hover:text-yellow-700"
                onClick={() => {
                  setEditingModule(module);
                  setFormOpen(true);
                }}
              >
                <Pencil className="w-5 h-5" />
              </button>
              <button
                className="text-red-600 hover:text-red-700"
                onClick={() => deleteModule(module.id)}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Subsection Manager */}
          <SubsectionAdmin module={module} />
        </div>
      ))}

      {/* Form Modal */}
      {formOpen && (
        <ModuleForm
          course={course}
          module={editingModule}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
};

const ModuleForm = ({ course, module, onClose }) => {
  const [form, setForm] = useState(
    module || { slug: "", title: "", description: "", order: 1 }
  );

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    const url = module?.id
      ? `${apiBaseURL}/modules/${module.id}`
      : `${apiBaseURL}/courses/${course.id}/modules`;
    const method = module?.id ? "PUT" : "POST";

    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] space-y-4 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {module ? "Edit Module" : "Create Module"}
        </h2>

        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => handleChange("slug", e.target.value)}
        />
        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Title"
          value={form.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />
        <textarea
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Description"
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
        />
        <input
          type="number"
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Order"
          value={form.order}
          onChange={(e) => handleChange("order", parseInt(e.target.value))}
        />

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            className="flex items-center gap-1 bg-gray-400 hover:bg-gray-500 text-white px-3 py-2 rounded-md text-sm"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-md text-sm"
            onClick={handleSubmit}
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleAdmin;
